import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ListChecks } from 'lucide-react';
import { type SchoolLevel } from '@jeraisonne/db';
import { cn } from '@jeraisonne/ui';
import { LeaderboardTabs } from '@/features/live-quiz/leaderboard-tabs';
import { liveCoverUrl } from '@/lib/live-covers';
import { LiveCardMeta, LiveCoverCard } from '@/features/live-ui/live-cover-card';
import { levelSubjectChip } from '@/features/live-ui/live-card-model';
import { useLiveEvents } from './use-live-events';
import { partitionLiveEvents, rankableChallenges, formatLiveEventShortDate } from './live-events';

/**
 * Onglet « Classements » : le classement multi-segments (National / Niveau /
 * Région / … via `LeaderboardTabs`) d'un défi déjà démarré du niveau. Défaut =
 * le plus récent ; un sélecteur de dates permet de remonter les défis passés.
 * `initialChallengeId` pré-sélectionne le défi ciblé depuis l'onglet Live School.
 */
export function ClassementsTab({
  level,
  userId,
  initialChallengeId,
}: {
  level: SchoolLevel | null;
  userId: string;
  initialChallengeId: string | null;
}) {
  const { data, isLoading } = useLiveEvents(level);

  const candidates = useMemo(() => {
    if (!data) return [];
    return rankableChallenges(
      partitionLiveEvents([...data.upcoming, ...data.past], data.server_time),
    );
  }, [data]);

  const [selectedId, setSelectedId] = useState<string | null>(initialChallengeId);
  useEffect(() => {
    if (initialChallengeId) setSelectedId(initialChallengeId);
  }, [initialChallengeId]);

  const effectiveId = selectedId ?? candidates[0]?.id ?? null;
  const selected = candidates.find((c) => c.id === effectiveId) ?? null;
  const campaignCover = selected?.campaign_id
    ? ((data?.campaigns ?? []).find((c) => c.id === selected.campaign_id)?.cover_url ?? null)
    : null;

  if (!level) {
    return (
      <p className="text-ink-600 py-8 text-center text-sm">
        Choisis un niveau pour voir les classements.
      </p>
    );
  }
  if (isLoading) {
    return <p className="text-ink-600 py-8 text-center text-sm">Chargement des classements…</p>;
  }
  if (!effectiveId) {
    return (
      <p className="text-ink-600 py-8 text-center text-sm">
        Aucun classement disponible pour ce niveau.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {candidates.length > 1 && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className={cn(
                'min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold transition active:scale-[0.98]',
                c.id === effectiveId
                  ? 'bg-linear-to-b from-[#2f86ff] to-[#0566f2] text-white'
                  : 'bg-ink-100 text-ink-600',
              )}
            >
              {formatLiveEventShortDate(c.starts_at)}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <LiveCoverCard
          size="md"
          badge={selected.status === 'live' ? 'live' : 'finished'}
          coverUrl={liveCoverUrl({ fallbackUrl: campaignCover, seed: selected.id })}
          chip={levelSubjectChip(selected.level, selected.subject)}
          title={selected.title ?? 'Live School'}
        >
          <LiveCardMeta
            className="mt-2"
            items={[
              { icon: CalendarDays, text: formatLiveEventShortDate(selected.starts_at) },
              { icon: ListChecks, text: `${selected.step_count} questions` },
            ]}
          />
        </LiveCoverCard>
      )}

      <LeaderboardTabs
        challengeId={effectiveId}
        niveau={level}
        userId={userId}
        topLimit={10}
        unit={selected?.scoring === 'hora' ? 'HORA' : 'pts'}
      />
    </div>
  );
}
